import React, { useEffect, useState, useCallback } from 'react'
import { FlatList, RefreshControl, useWindowDimensions } from 'react-native'
import { ArrowLeft, Store } from 'lucide-react-native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RootStackParamList } from '../types/navigation'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { EmptyState, ProductCard, Spinner, Text, TopBar, YStack } from '@fliisi/pollen'
import { useLanguage } from '@fliisi/i18n'

interface Product {
  id: string; title: string; price: number; imageUrl: string | null; storeName: string | null
}
interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'Discoveries'>
}

const GRID_PADDING = 20
const CARD_GAP = 12

export default function DiscoveriesScreen({ navigation }: Props) {
  const { width: screenWidth } = useWindowDimensions()
  const contentWidth = Math.min(screenWidth, 768)
  const numColumns = contentWidth > 500 ? 3 : 2
  const cardWidth = (contentWidth - GRID_PADDING * 2 - CARD_GAP * (numColumns - 1)) / numColumns
  const { t } = useLanguage()
  const { session } = useAuth()
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set())

  const fetchProducts = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('products')
        .select('id, title, title_fi, title_en, price, created_at, product_images(image_url), flea_market:flea_markets(name)')
        .eq('status', 'for_sale').not('price', 'is', null)
        .order('created_at', { ascending: false }).limit(50)
      if (data) {
        setProducts(data.map((p: any) => {
          const market = Array.isArray(p.flea_market) ? p.flea_market[0] : p.flea_market
          return { id: p.id, title: p.title_fi || p.title_en || p.title || '', price: p.price || 0, imageUrl: p.product_images?.[0]?.image_url || null, storeName: market?.name || null }
        }))
      }
    } catch (e) { console.error('Discoveries fetch error:', e) }
    finally { setLoading(false); setRefreshing(false) }
  }, [])

  const fetchFavorites = useCallback(async () => {
    if (!session?.user?.id) return
    try {
      const { data } = await supabase.from('favorites').select('product_id').eq('user_id', session.user.id)
      if (data) setFavoriteIds(new Set(data.map((f: any) => f.product_id)))
    } catch (e) { console.error('Favorites fetch error:', e) }
  }, [session?.user?.id])

  useEffect(() => { fetchProducts(); fetchFavorites() }, [])

  const toggleFavorite = async (productId: string) => {
    if (!session?.user?.id) { navigation.navigate('Login'); return }
    const isFav = favoriteIds.has(productId)
    setFavoriteIds(prev => { const next = new Set(prev); isFav ? next.delete(productId) : next.add(productId); return next })
    try {
      if (isFav) await supabase.from('favorites').delete().eq('user_id', session.user.id).eq('product_id', productId)
      else await supabase.from('favorites').insert({ user_id: session.user.id, product_id: productId })
    } catch { setFavoriteIds(prev => { const next = new Set(prev); isFav ? next.add(productId) : next.delete(productId); return next }) }
  }

  const renderProduct = ({ item }: { item: Product }) => {
    const isFav = favoriteIds.has(item.id)
    return (
      <ProductCard
        w={cardWidth}
        imageUri={item.imageUrl}
        title={item.title}
        price={`${item.price.toFixed(2)} €`}
        shopIcon={item.storeName ? <Store size={10} color="#858B80" /> : undefined}
        shopName={item.storeName || undefined}
        isFavorited={isFav}
        onPress={() => navigation.navigate('ProductView', { productId: item.id })}
        onHeartPress={() => toggleFavorite(item.id)}
      />
    )
  }

  return (
    <YStack f={1} bg="$background">
      <TopBar
        title={t('finds_for_you')}
        leftIcon={<ArrowLeft size={20} color="#748C70" />}
        onLeftPress={() => navigation.goBack()}
      />
      <Text preset="bodySmall" tone="secondary" px="$lg" mt={4} mb="$md">
        {t('products_that_interest_you')}
      </Text>

      {loading ? (
        <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
      ) : (
        <FlatList
          key={`grid-${numColumns}`}
          data={products}
          keyExtractor={item => item.id}
          renderItem={renderProduct}
          numColumns={numColumns}
          columnWrapperStyle={{ gap: CARD_GAP, marginBottom: CARD_GAP }}
          contentContainerStyle={{ paddingHorizontal: GRID_PADDING, paddingBottom: 16, maxWidth: 768, alignSelf: 'center', width: '100%' }}
          showsVerticalScrollIndicator={false}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); fetchProducts() }} />}
          ListEmptyComponent={<EmptyState title={t('no_products')} />}
        />
      )}
    </YStack>
  )
}
